/**
 * NOT CURRENTLY USED.
 *
 * The teacher portal navigation is attendance-only (see NAV_ITEMS in StaffLayout), so
 * this page is unreachable from the UI. Its route is still registered in StaffApp, so
 * direct links resolve and re-enabling it only takes restoring its nav entry.
 *
 * It has not been carried through the mobile-first attendance rework: it is desktop-
 * oriented and predates the school/grade roster segmentation. Left as-is on purpose —
 * do not treat it as a current example of how these screens should look.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { StaffLayout } from '../StaffLayout';
import { isBhcsStudentWaiverSigned, isBhcsStudentWaiverOptOut } from '../../types/waiver';
import { NON_TEACHING_TYPES_FILTER } from '../programSessions';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
}

interface StaffStudent {
  id: string;
  first_name: string;
  last_name: string;
  active: boolean;
  metadata: { school?: string; grade?: string; bhcs_waiver?: unknown; labels?: string[] } | null;
  programNames: string[];
  parents: { name: string; email: string; phone: string | null; isPrimary: boolean }[];
}

interface Props {
  staffUser: StaffUser;
}

function WaiverBadge({ waiver }: { waiver: unknown }) {
  if (isBhcsStudentWaiverOptOut(waiver)) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">Opt-out</span>;
  }
  if (isBhcsStudentWaiverSigned(waiver) || waiver) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800">Signed</span>;
  }
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">Not signed</span>;
}

export function StaffStudents({ staffUser }: Props) {
  const [search, setSearch] = useState('');
  const scopedIds = staffUser.program_ids;

  const { data: students = [], isLoading, error } = useQuery<StaffStudent[]>({
    queryKey: ['staff-students', scopedIds],
    queryFn: async () => {
      // Step 1: resolve the teacher's active programs.
      let pq = supabase
        .from('programs')
        .select('id, name')
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER);
      if (scopedIds) pq = pq.in('id', scopedIds);
      const { data: progs, error: pErr } = await pq;
      if (pErr) throw pErr;
      const programs = progs ?? [];
      if (programs.length === 0) return [];

      // Step 2: authoritative roster per program via staff_camp_roster RPC. For camps this
      // also folds in Full Summer Pass students (mapped to the camps their pass overlaps),
      // which a plain enrollments query on the week programs misses. Build
      // studentId -> { programs, viaSummerPass }.
      const rosters = await Promise.all(
        programs.map(p =>
          supabase.rpc('staff_camp_roster', { p_program_id: p.id }).then(r => {
            if (r.error) throw r.error;
            return { name: p.name, rows: (r.data ?? []) as { student_id: string; via_summer_pass: boolean }[] };
          }),
        ),
      );
      const meta = new Map<string, { programs: Set<string>; viaSummerPass: boolean }>();
      for (const { name, rows } of rosters) {
        for (const row of rows) {
          const entry = meta.get(row.student_id) ?? { programs: new Set<string>(), viaSummerPass: false };
          entry.programs.add(name);
          entry.viaSummerPass = entry.viaSummerPass || row.via_summer_pass;
          meta.set(row.student_id, entry);
        }
      }
      const studentIds = Array.from(meta.keys());
      if (studentIds.length === 0) return [];

      // Step 3: full student details + all parents. Teachers are RLS-allowed to
      // read these (staff_has_enrollment_access covers any active enrollment).
      const { data: studentRows, error: sErr } = await supabase
        .from('students')
        .select(`
          id, first_name, last_name, active, metadata,
          student_parents(
            is_primary,
            parents!inner(id, first_name, last_name, email, phone)
          )
        `)
        .in('id', studentIds);
      if (sErr) throw sErr;

      return (studentRows ?? []).map(row => {
        const s = row as unknown as {
          id: string; first_name: string; last_name: string; active: boolean;
          metadata: StaffStudent['metadata'];
          student_parents: { is_primary: boolean; parents: { id: string; first_name: string; last_name: string; email: string; phone: string | null } }[];
        };
        const parents = (s.student_parents ?? [])
          .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
          .map(sp => ({
            name: [sp.parents.first_name, sp.parents.last_name].filter(Boolean).join(' '),
            email: sp.parents.email,
            phone: sp.parents.phone,
            isPrimary: sp.is_primary,
          }));
        const m = meta.get(s.id)!;
        // Pass students attend every overlapping camp week — collapse to a single
        // "Full Summer Pass" chip instead of listing each week.
        const programNames = m.viaSummerPass ? ['Full Summer Pass'] : Array.from(m.programs).sort();
        return {
          id: s.id,
          first_name: s.first_name,
          last_name: s.last_name,
          active: s.active,
          metadata: s.metadata,
          programNames,
          parents,
        };
      }).sort((a, b) =>
        a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name)
      );
    },
  });

  const filtered = students.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.first_name.toLowerCase().includes(q) ||
      s.last_name.toLowerCase().includes(q) ||
      s.parents.some(p => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)) ||
      s.metadata?.school?.toLowerCase().includes(q)
    );
  });

  return (
    <StaffLayout staffUser={staffUser}>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Students</h1>
        {!isLoading && (
          <span className="text-sm text-gray-500">{filtered.length} student{filtered.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, parent, or school…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full sm:w-80 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-lg" />)}</div>
      ) : error ? (
        <p className="text-red-500 text-sm">Failed to load students.</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500 text-sm">{search ? 'No students match.' : 'No students found.'}</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Desktop table — mirrors AdminStudents layout */}
          <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Student Info', 'Waiver', 'Parents', 'Programs'].map(h => (
                  <th key={h} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {s.first_name} {s.last_name}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="space-y-0.5">
                      {s.metadata?.grade && s.metadata.grade !== 'Not specified' && (
                        <div className="text-gray-900">Grade: {s.metadata.grade}</div>
                      )}
                      {s.metadata?.school && s.metadata.school !== 'Not specified' && (
                        <div className="text-gray-900">School: {s.metadata.school}</div>
                      )}
                      {s.metadata?.labels && s.metadata.labels.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {s.metadata.labels.map(l => (
                            <span key={l} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{l}</span>
                          ))}
                        </div>
                      )}
                      {!s.metadata?.grade && !s.metadata?.school && (
                        <div className="text-gray-400 text-xs">—</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <WaiverBadge waiver={s.metadata?.bhcs_waiver} />
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="space-y-1.5">
                      {s.parents.length ? s.parents.map(p => (
                        <div key={p.email}>
                          <div className="font-medium text-gray-900">
                            {p.name}
                            {p.isPrimary && <span className="ml-1.5 text-xs text-blue-600">(Primary)</span>}
                          </div>
                          <div className="text-gray-500">{p.email}</div>
                        </div>
                      )) : <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {s.programNames.map(name => (
                        <span key={name} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{name}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-gray-100">
            {filtered.map(s => (
              <div key={s.id} className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-gray-900">{s.first_name} {s.last_name}</p>
                  <WaiverBadge waiver={s.metadata?.bhcs_waiver} />
                </div>
                {(s.metadata?.grade || s.metadata?.school) && (
                  <p className="text-xs text-gray-500 mb-1">
                    {[s.metadata?.grade, s.metadata?.school].filter(Boolean).join(' · ')}
                  </p>
                )}
                <div className="flex flex-wrap gap-1 mb-2">
                  {s.programNames.map(name => (
                    <span key={name} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">{name}</span>
                  ))}
                </div>
                <div className="space-y-1.5">
                  {s.parents.map(p => (
                    <div key={p.email} className="text-sm space-y-0.5">
                      <p className="text-gray-700 font-medium">
                        {p.name}
                        {p.isPrimary && <span className="ml-1.5 text-xs text-blue-500">(Primary)</span>}
                      </p>
                      <a href={`mailto:${p.email}`} className="text-blue-600 hover:underline block text-xs">{p.email}</a>
                      {p.phone && <a href={`tel:${p.phone}`} className="text-blue-600 hover:underline block text-xs">{p.phone}</a>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
